import { useRef, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, ToggleButton as MuiToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { AxiosError } from 'axios';
import {
  CellEditRequestEvent,
  ColDef,
  ColumnResizedEvent,
  GridApi,
  IRowNode,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { InfiniteScrollingGridSelectAllHeader } from '../AgGrid/InfiniteScrollingGridSelectAllHeader';
import { useSearchArcgisFeatureClasses } from '../../hooks/api/use-arcgis-feature-classes.hook';
import { useSearchAutodeskLayers } from '../../hooks/api/use-autodesk-layers.hook';
import { AutodeskLayerRelationship, WithLayer } from '../../../../types/models/autodesk-layer.model';
import { InfniteScrollingGrid } from '../AgGrid/InfiniteScrollingGrid';
import { LinkCellEditor } from '../AgGrid/CellEditors/LinkCellEditor';
import {
  ArcgisCivil3dSettingApiModel,
  WithArcgisFeatureClass,
  WithAutodeskLayer,
  WithConfiguration,
} from '../../../../types/models/arcgis-civil3d-setting.model';
import { WithTemplate } from '../../../../types/models/arcgis-civil3d-setting-configuration.model';
import { EditQuery } from './EditQuery';
import { Civil3dFeatureType } from '../../../../types/constants/civil3d-feature-type';
import { useConfirm } from '../../contexts/confirm.context';
import EditArcgisCivil3dSettingConfigurationDetails from './Configurations/EditArcgisCivil3dSettingConfigurationDetails';
import { useGetSettingConfiguration } from '../../hooks/api/use-arcgis-civil3d-setting-configurations.hook';

type BaseRowType = ArcgisCivil3dSettingApiModel & WithConfiguration & WithArcgisFeatureClass;

type Props<T> = {
  featureType: Civil3dFeatureType;
  loading: boolean;
  error?: AxiosError<{ message: string }> | null;
  columns: ColDef<T>[];
  getSetting: (settingId: number) => Promise<ArcgisCivil3dSettingApiModel>;
  onUpdateQuery: (settingId: number, query: string) => Promise<void>;
  onGetRows: Parameters<typeof InfniteScrollingGrid<T>>[0]['onGetRows'];
  onUpdateRow: (event: CellEditRequestEvent) => Promise<void>;
  onColumnResized: (event: ColumnResizedEvent) => void;
  onBulkDelete: (settingIds: number[]) => Promise<void>;
  onBulkDuplicate: (settingIds: number[]) => Promise<void>;
};

const ToggleButton = styled(MuiToggleButton)(({ theme }) => ({
  '&.Mui-selected, &.Mui-selected:hover': {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
  },
}));

export const ArcgisCivil3dConfigurationGrid = <T extends BaseRowType>({
  featureType,
  loading,
  error,
  columns: childColumns,
  getSetting,
  onUpdateQuery,
  onGetRows,
  onUpdateRow,
  onColumnResized,
  onBulkDelete,
  onBulkDuplicate,
}: Props<T>) => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();
  const { configurationSettingId } = useParams<{ configurationSettingId: string }>();
  const gridApi = useRef<GridApi>();
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [queryRowId, setQueryRowId] = useState<number | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [selectedRows, setSelectedRows] = useState<IRowNode<T>[]>([]);
  const [editing, setEditing] = useState(false);

  const { settingConfiguration, error: settingConfigurationError } = useGetSettingConfiguration({
    settingConfigurationId: Number(configurationSettingId),
  });

  const { loading: loadingFeatureClasses, response: featureClassesResponse } = useSearchArcgisFeatureClasses({
    limit: 999999,
  });
  const { loading: loadingAutodeskLayers, response: autodeskLayersResponse } = useSearchAutodeskLayers({
    limit: 999999,
    include: [AutodeskLayerRelationship.LAYER],
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });

  const featureClassIdNameMap = featureClassesResponse?.pages
    .flatMap(({ result }) => result)
    .reduce((acc, featureClass) => ({ ...acc, [featureClass.id]: featureClass.name }), {} as Record<string, string>);

  const autodeskLayerIdNameMap = autodeskLayersResponse?.pages
    .flatMap(({ result }) => result)
    .reduce(
      (acc, autodeskLayer) => ({ ...acc, [autodeskLayer.id]: (autodeskLayer as WithLayer).layer.name }),
      {} as Record<string, string>,
    );

  const handleSelectionChange = ({ api }: Pick<SelectionChangedEvent, 'api'>) => {
    setSelectedRows(api.getSelectedRows());
  };

  const handleColumnResized = (params: ColumnResizedEvent) => {
    setColumnWidths((prevColumnWidths) =>
      params.column
        ? {
            ...(prevColumnWidths ?? {}),
            [params.column.getColId()]: params.column.getActualWidth(),
          }
        : prevColumnWidths,
    );

    onColumnResized(params);
  };

  const handleBulkDelete = async (settingIds: number[]) => {
    if (settingIds.length === 0) return;

    showConfirm({
      onConfirm: async () => {
        await onBulkDelete(settingIds);

        const nodes: IRowNode[] = [];

        gridApi.current?.forEachNode((row) => nodes.push(row));
        gridApi.current?.setNodesSelected({ nodes, newValue: false });

        setSelectedRows([]);

        gridApi.current?.refreshInfiniteCache();
      },

      title: `Delete ArcGIS Civil 3D Settings`,
      message: `Are you sure you want to delete ${settingIds.length} setting(s)?`,
      error: error?.message,
    });
  };

  const handleBulkDuplicate = async (settingIds: number[]) => {
    if (settingIds.length === 0) return;

    showConfirm({
      onConfirm: async () => {
        await onBulkDuplicate(settingIds);
        const nodes: IRowNode[] = [];

        gridApi.current?.forEachNode((row) => nodes.push(row));
        gridApi.current?.setNodesSelected({ nodes, newValue: false });

        setSelectedRows([]);

        gridApi.current?.refreshInfiniteCache();
      },
      title: `Duplicate ArcGIS Civil 3D Settings`,
      message: `Are you sure you want to duplicate ${selectedRows.length} setting(s)?`,
      error: error?.message,
    });
  };

  const columns: ColDef<T | (BaseRowType & WithAutodeskLayer)>[] = [
    {
      headerName: 'Template Name',
      field: 'configuration',
      colId: 'configuration.template.name',
      width: columnWidths['configuration.template.name'] ? Number(columnWidths['configuration.template.name']) : 200,
      flex: 0,
      valueGetter: ({ data }: { data?: BaseRowType }) =>
        data?.configuration ? (data.configuration as WithTemplate).template?.name : '',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'name',
      headerName: 'Setting Name',
      width: columnWidths.name ? Number(columnWidths.name) : 350,
      flex: 0,
      valueGetter: ({ data }: { data?: BaseRowType }) => data?.name,
      editable: true,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Source Layer',
      field: 'arcgisFeatureClass',
      colId: 'arcgisFeatureClass.name',
      width: columnWidths['arcgisFeatureClass.name'] ? Number(columnWidths['arcgisFeatureClass.name']) : 200,
      flex: 0,
      valueGetter: ({ data }: { data?: BaseRowType }) => data?.arcgisFeatureClass?.id,
      editable: true,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: featureClassesResponse?.pages.flatMap(({ result }) => result).map(({ id }) => id),
        formatValue: (value: number) => featureClassIdNameMap?.[value] ?? '',
      },
      valueFormatter: (params: { value: number }) => featureClassIdNameMap?.[params.value] ?? '',
    },
    {
      field: 'query',
      width: columnWidths.query ? Number(columnWidths.query) : 200,
      flex: 0,
      valueGetter: ({ data }: { data?: BaseRowType }) => data?.query,
      editable: true,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: LinkCellEditor,
      cellEditorParams: {
        text: 'Edit Query',
        onClick: (currentRowId: number | null) => setQueryRowId(currentRowId),
      },
    },
    {
      field: 'priority',
      width: columnWidths.priority ? Number(columnWidths.priority) : 100,
      flex: 0,
      valueGetter: ({ data }: { data?: BaseRowType }) => data?.priority,
      editable: true,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { min: 0, max: 1000 },
    },
    {
      headerName: 'Layer',
      field: 'autodeskLayer.name',
      colId: 'autodeskLayer.name',
      width: columnWidths['autodeskLayer.name'] ? Number(columnWidths['autodeskLayer.name']) : 200,
      flex: 0,
      valueGetter: ({ data }: { data?: BaseRowType }) => data?.autodeskLayer?.id,
      editable: true,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: autodeskLayersResponse?.pages.flatMap(({ result }) => result).map(({ id }) => id) ?? [],
        formatValue: (value: number) => autodeskLayerIdNameMap?.[value] ?? '',
      },
      valueFormatter: (params: { value: number }) => autodeskLayerIdNameMap?.[params.value] ?? '',
    },
    ...childColumns,
  ];

  return (
    <>
      <Outlet />
      <Box display="flex" sx={{ m: '10px 0' }} justifyContent="space-between">
        <Box display="flex" justifyContent="space-between" width="100%">
          <ToggleButtonGroup value={featureType} exclusive aria-label="ArcGIS Civil 3D feature type">
            <ToggleButton
              value={Civil3dFeatureType.COGO_POINT}
              onClick={() => navigate('../cogo-points')}
              color="primary"
            >
              COGO POINTS
            </ToggleButton>
            <ToggleButton
              value={Civil3dFeatureType.FEATURE_LINE}
              onClick={() => navigate('../feature-lines')}
              color="primary"
            >
              FEATURE LINES
            </ToggleButton>
            <ToggleButton
              value={Civil3dFeatureType.ALIGNMENT}
              onClick={() => navigate('../alignments')}
              color="primary"
            >
              ALIGNMENTS
            </ToggleButton>
            <ToggleButton value={Civil3dFeatureType.PARCEL} onClick={() => navigate('../parcels')} color="primary">
              PARCELS
            </ToggleButton>
          </ToggleButtonGroup>
          <Box display="flex" gap={1.5}>
            <Button
              color="primary"
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={() => handleBulkDelete(selectedRows.map(({ id }) => Number(id)))}
            >
              Delete
            </Button>
            <Button color="primary" variant="contained" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
              Rename
            </Button>
            <Button
              color="primary"
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={() => handleBulkDuplicate(selectedRows.map(({ id }) => Number(id)))}
            >
              Duplicate
            </Button>
            <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('add')}>
              Add
            </Button>
          </Box>
        </Box>
      </Box>

      <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
        {error || settingConfigurationError ? (
          <Alert severity="error">{(error || settingConfigurationError)?.response?.data.message}</Alert>
        ) : null}
        <InfniteScrollingGrid
          onGridReady={({ api }) => {
            gridApi.current = api;
          }}
          readOnlyEdit
          loading={loadingAutodeskLayers || loadingFeatureClasses || loading}
          columnDefs={columns}
          selectionColumnDef={{
            pinned: 'left',
            headerComponent: InfiniteScrollingGridSelectAllHeader,
            headerComponentParams: { onSelectAll: (value: boolean) => setIsSelectAll(value), isSelectAll },
          }}
          rowSelection={{
            mode: 'multiRow',
            selectAll: 'filtered',
          }}
          onGetRows={onGetRows}
          onCellEditRequest={(event) => {
            onUpdateRow(event);
            event.api.purgeInfiniteCache();
          }}
          onSelectionChanged={handleSelectionChange}
          onModelUpdated={handleSelectionChange}
          onColumnResized={handleColumnResized}
        />
        {queryRowId && (
          <EditQuery
            isOpen
            error={error}
            getSetting={() => getSetting(queryRowId)}
            onUpdateQuery={(query) => onUpdateQuery(queryRowId, query)}
            onClose={() => {
              gridApi.current?.refreshInfiniteCache();
              gridApi.current?.stopEditing();
              setQueryRowId(null);
            }}
          />
        )}
      </Box>
      <EditArcgisCivil3dSettingConfigurationDetails
        isOpen={editing && !!settingConfiguration}
        onClose={() => setEditing(false)}
        settingConfiguration={settingConfiguration}
      />
    </>
  );
};
